<template>
  <div class="tab-pane fade show active">
    <LoadingIndicatorComponent v-if="loading" message="Loading price data..." />
    <div v-else class="card">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <div>
          <h5 class="mb-0">EAC Price Trends & History</h5>
          <small class="text-muted">Historical price trends by vintage</small>
        </div>
        <button class="btn btn-primary" @click="showAddPriceModal = true">
          <i class="fas fa-plus me-2"></i>Add Price
        </button>
      </div>
      <div class="card-body">
        <!-- Source Filters -->
        <div class="mb-3">
          <label class="form-label d-block">Sources</label>
          <div class="btn-group" role="group" aria-label="Source filters">
            <button
              v-for="source in availableSources"
              :key="source"
              type="button"
              class="btn"
              :class="
                enabledSources.includes(source)
                  ? 'btn-primary'
                  : 'btn-outline-secondary'
              "
              @click="toggleSource(source)"
            >
              {{ source }}
            </button>
          </div>
          <small class="text-muted d-block mt-1">
            Toggle data sources. Products from disabled sources are hidden.
          </small>
        </div>

        <!-- Price Filters -->
        <PriceFiltersComponent
          v-model:filters="priceFilters"
          v-model:chartFilters="chartFilters"
          :availableVintages="availableVintages"
          :availableNames="filteredAvailableNames"
        />

        <!-- Range Selector -->
        <RangeSelectorComponent
          v-if="rangeLabels.length > 0"
          :datasets="rangeDatasets"
          :labels="rangeLabels"
          :onRangeChange="handleRangeChange"
          class="mb-4"
        />

        <!-- Price Trend Chart -->
        <PriceChartComponent
          :filteredPrices="filteredPriceHistory"
          :selectedRangeStart="selectedRangeStart"
          :selectedRangeEnd="selectedRangeEnd"
          class="mb-4"
          @update:chartData="updateRangeData"
        />

        <!-- Price Table -->
        <PriceTableComponent
          :filteredPrices="filteredPriceHistory"
          :loading="loading"
          :error="error"
          :deletingIds="deletingPrices"
          @price-deleted="fetchPriceHistory"
          @delete-start="deletingPrices.add($event)"
          @delete-end="deletingPrices.delete($event)"
        />
      </div>
    </div>

    <!-- Add Price Modal -->
    <AddPriceModalComponent
      v-model="showAddPriceModal"
      @price-added="fetchPriceHistory"
    />
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";

import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import AddPriceModalComponent from "@/market_insights/admin/AddPriceModalComponent.vue";
import PriceChartComponent from "@/market_insights/admin/PriceChartComponent.vue";
import PriceFiltersComponent from "@/market_insights/admin/PriceFiltersComponent.vue";
import PriceTableComponent from "@/market_insights/admin/PriceTableComponent.vue";
import RangeSelectorComponent from "@/market_insights/admin/RangeSelectorComponent.vue";
import type { EacPrice } from "@/market_insights/admin/eacPricingTypes";

const { globalErrorHandler } = useGlobalHandlers();

// State
const priceHistory = ref<EacPrice[]>([]);
const availableNames = ref<{ name: string; source: string }[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const deletingPrices = ref(new Set<number>());
const showAddPriceModal = ref(false);

// Filters
const priceFilters = ref({
  names: [] as string[],
});

const chartFilters = ref({
  vintages: [] as string[],
});

// Source filters: GXGlobal is noisy, so it starts disabled; any other
// source (including newly discovered ones) is enabled by default
const KNOWN_SOURCES = ["provider1", "provider1_rego", "GXGlobal"];
const disabledSources = ref<string[]>(["GXGlobal"]);

const availableSources = computed(() => {
  const inData = [...new Set(priceHistory.value.map((price) => price.source))];
  return [
    ...KNOWN_SOURCES.filter((source) => inData.includes(source)),
    ...inData.filter((source) => !KNOWN_SOURCES.includes(source)).sort(),
  ];
});

const enabledSources = computed(() =>
  availableSources.value.filter(
    (source) => !disabledSources.value.includes(source)
  )
);

const toggleSource = (source: string) => {
  if (disabledSources.value.includes(source)) {
    disabledSources.value = disabledSources.value.filter((s) => s !== source);
  } else {
    disabledSources.value = [...disabledSources.value, source];
    priceFilters.value = {
      ...priceFilters.value,
      names: priceFilters.value.names.filter(
        (key) => key.split("|||")[1] !== source
      ),
    };
  }
};

const filteredAvailableNames = computed(() =>
  availableNames.value.filter((option) =>
    enabledSources.value.includes(option.source)
  )
);

// Range selector state
const selectedRangeStart = ref<number>(0);
const selectedRangeEnd = ref<number>(0);
const rangeLabels = ref<string[]>([]);
const rangeDatasets = ref<any[]>([]);

// Computed properties
// Vintages offered in the filter come from the SELECTED products only, so
// year vintages (GO) and compliance periods (REGO "CP24") don't mix
const availableVintages = computed(() => {
  const selectedPrices = priceHistory.value.filter((price) =>
    priceFilters.value.names.includes(`${price.name}|||${price.source}`)
  );
  const vintages = [
    ...new Set(
      selectedPrices
        .map((price) => price.vintage)
        .filter((v): v is string => v !== null)
    ),
  ].sort((a, b) => b.localeCompare(a));
  return vintages;
});

const filteredPriceHistory = computed(() => {
  let filtered = priceHistory.value;

  if (priceFilters.value.names.length === 0) {
    return [];
  }

  filtered = filtered.filter((price) => {
    const key = `${price.name}|||${price.source}`;
    return priceFilters.value.names.includes(key);
  });

  const hasProductsWithVintages = filtered.some(
    (price) => price.vintage !== null
  );

  if (hasProductsWithVintages) {
    if (chartFilters.value.vintages.length === 0) {
      return [];
    }

    filtered = filtered.filter((price) =>
      price.vintage !== null
        ? chartFilters.value.vintages.includes(price.vintage)
        : true
    );
  }

  return filtered;
});

const YEAR_VINTAGE_RE = /^\d{4}$/;

const setDefaultDateFilters = () => {
  if (availableVintages.value.length === 0) {
    chartFilters.value = { ...chartFilters.value, vintages: [] };
    return;
  }

  // Default to the years around now, plus ALL non-year vintages (e.g. REGO
  // compliance periods like "CP24") so products without year vintages show up
  const currentYear = new Date().getFullYear();
  const yearDefaults = [
    String(currentYear - 1),
    String(currentYear),
    String(currentYear + 1),
  ].filter((year) => availableVintages.value.includes(year));
  const nonYearVintages = availableVintages.value.filter(
    (vintage) => !YEAR_VINTAGE_RE.test(vintage)
  );

  const defaults = [...yearDefaults, ...nonYearVintages];
  chartFilters.value = {
    ...chartFilters.value,
    vintages: defaults.length > 0 ? defaults : [...availableVintages.value],
  };
};

// Re-derive vintage defaults whenever the selected products change
watch(() => priceFilters.value.names, setDefaultDateFilters);

const fetchAvailableNames = () => {
  const uniqueCombinations = new Map<
    string,
    { name: string; source: string }
  >();

  priceHistory.value.forEach((price) => {
    const key = `${price.name}|||${price.source}`;
    if (!uniqueCombinations.has(key)) {
      uniqueCombinations.set(key, {
        name: price.name,
        source: price.source,
      });
    }
  });

  availableNames.value = Array.from(uniqueCombinations.values()).sort(
    (a, b) => {
      if (a.name !== b.name) {
        return a.name.localeCompare(b.name);
      }
      return a.source.localeCompare(b.source);
    }
  );

  if (priceFilters.value.names.length === 0) {
    const defaultOption = availableNames.value.find(
      (opt) => opt.name === "GO - Nordic hydro" && opt.source === "provider1"
    );
    if (defaultOption) {
      priceFilters.value.names = [
        `${defaultOption.name}|||${defaultOption.source}`,
      ];
    }
  }
};

const fetchPriceHistory = async () => {
  loading.value = true;
  error.value = null;
  try {
    const response = await goSolidApi.get("/api/admin/eac-pricing/prices");
    priceHistory.value = response.data.data;

    fetchAvailableNames();

    nextTick(() => {
      setDefaultDateFilters();
    });
  } catch (err: any) {
    error.value = "Failed to fetch price history";
    globalErrorHandler(err);
  } finally {
    loading.value = false;
  }
};

const handleRangeChange = (startIndex: number, endIndex: number) => {
  selectedRangeStart.value = startIndex;
  selectedRangeEnd.value = endIndex;
};

const updateRangeData = (data: { labels: string[]; datasets: any[] }) => {
  rangeLabels.value = data.labels;
  rangeDatasets.value = data.datasets;
};

onMounted(async () => {
  await fetchPriceHistory();
});
</script>
